import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/http";
import { useT } from "@/i18n/I18nContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Calculator, Sparkles, Info } from "lucide-react";

const JOBS = [
  { key: "sitzend", labelKey: "calc.jobSitting" },
  { key: "stehend", labelKey: "calc.jobStanding" },
  { key: "koerperlich", labelKey: "calc.jobPhysical" },
];

const STEP_OPTIONS = [
  { value: 4000, label: "< 5'000" },
  { value: 6500, label: "5–8'000" },
  { value: 10000, label: "8–12'000" },
  { value: 13000, label: "> 12'000" },
];

const TRAIN_OPTIONS = [
  { value: 0, label: "0" },
  { value: 2, label: "1–2×" },
  { value: 4, label: "3–4×" },
  { value: 6, label: "5+×" },
];

const GOALS = [
  { key: "dimagrimento", labelKey: "calc.goalCut", detail: "−10…20%" },
  { key: "mantenimento", labelKey: "calc.goalMaintain", detail: "±0" },
  { key: "performance", labelKey: "calc.goalPerf", detail: "+5%" },
  { key: "massa", labelKey: "calc.goalBulk", detail: "+15%" },
];

const GOAL_PRESETS = { dimagrimento: -15, mantenimento: 0, performance: 5, massa: 15 };

const DEFICITS = [
  { value: -10, labelKey: "calc.deficitSoft" },
  { value: -15, labelKey: "calc.deficitModerate" },
  { value: -20, labelKey: "calc.deficitFast" },
];

// Mirror of backend activity_factor_from_questions (nutrition_math.py)
function liveFactor(job, steps, trainings) {
  let base = { sitzend: 1.2, stehend: 1.35, koerperlich: 1.5 }[job] ?? 1.2;
  const s = steps || 0;
  if (s >= 12000) base += 0.15;
  else if (s >= 8000) base += 0.1;
  else if (s >= 5000) base += 0.05;
  base += Math.min(0.15, Math.max(0, trainings || 0) * 0.025);
  return Math.round(Math.max(1.2, Math.min(2.0, base)) * 1000) / 1000;
}

function ageFromBirthdate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
  return age > 0 && age < 120 ? age : "";
}

export default function MacroCalculatorDialog({ open, onOpenChange, onApplied, onManualEntry, onOpenPlan }) {
  const { t } = useT();
  const [form, setForm] = useState({
    gender: "male", age: "", height_cm: "", weight_kg: "",
    target_weight_kg: "",
    job_activity: "sitzend", steps_per_day: 6500, trainings_per_week: 0,
    goal: "mantenimento",
    adjustment_pct: 0,
    protein_pct: null, carbs_pct: null, fat_pct: null,
  });
  const [macrosEdited, setMacrosEdited] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [{ data: p }, { data: weights }] = await Promise.all([
          api.get("/client/profile"),
          api.get("/client/weight"),
        ]);
        const w = weights?.length ? weights[weights.length - 1].weight_kg : p.starting_weight_kg;
        const ap = p.activity_profile || {};
        setForm((f) => ({
          ...f,
          gender: p.gender === "female" || p.gender === "F" ? "female" : "male",
          age: ageFromBirthdate(p.birthdate) || f.age,
          height_cm: p.height_cm || f.height_cm,
          weight_kg: w || f.weight_kg,
          target_weight_kg: p.target_weight_kg ?? f.target_weight_kg,
          job_activity: ap.job_activity || f.job_activity,
          steps_per_day: ap.steps_per_day ?? f.steps_per_day,
          trainings_per_week: ap.trainings_per_week ?? f.trainings_per_week,
          goal: GOAL_PRESETS[p.goal] !== undefined ? p.goal : f.goal,
          adjustment_pct: GOAL_PRESETS[p.goal] !== undefined ? GOAL_PRESETS[p.goal] : f.adjustment_pct,
        }));
      } catch { /* ignore */ }
    })();
    setResult(null);
    setMacrosEdited(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const canCalc = useMemo(() => (
    form.age && form.height_cm && form.weight_kg &&
    +form.age >= 10 && +form.height_cm >= 100 && +form.weight_kg >= 30
  ), [form]);

  const factor = liveFactor(form.job_activity, form.steps_per_day, form.trainings_per_week);
  const isCustomGoal = form.goal === "custom";
  const isCustomDeficit = form.goal === "dimagrimento" && !DEFICITS.some((d) => d.value === +form.adjustment_pct);

  const setGoal = (key) => {
    setForm({ ...form, goal: key, adjustment_pct: GOAL_PRESETS[key] });
  };

  // Manual slider move: keep "Abnehmen" only while it is still a deficit
  const onSlider = (v) => {
    setForm((f) => {
      const preset = GOAL_PRESETS[f.goal];
      if (f.goal === "dimagrimento" && v < 0) return { ...f, adjustment_pct: v };
      if (preset !== undefined && v === preset) return { ...f, adjustment_pct: v };
      return { ...f, goal: "custom", adjustment_pct: v };
    });
  };

  const calculate = async (apply = false) => {
    if (!canCalc) return toast.error(t("calc.missing"));
    const setter = apply ? setApplying : setLoading;
    setter(true);
    try {
      const body = {
        gender: form.gender,
        age: +form.age,
        height_cm: +form.height_cm,
        weight_kg: +form.weight_kg,
        job_activity: form.job_activity,
        steps_per_day: +form.steps_per_day,
        trainings_per_week: +form.trainings_per_week,
        goal: form.goal,
        adjustment_pct: +form.adjustment_pct,
        apply,
      };
      if (macrosEdited && form.protein_pct != null && form.carbs_pct != null && form.fat_pct != null) {
        body.protein_pct = +form.protein_pct;
        body.carbs_pct = +form.carbs_pct;
        body.fat_pct = +form.fat_pct;
      }
      const { data } = await api.post("/client/calc-macros", body);
      setResult(data);
      if (apply && form.target_weight_kg !== "" && form.target_weight_kg != null) {
        const tw = parseFloat(String(form.target_weight_kg).replace(",", "."));
        if (Number.isFinite(tw) && tw >= 30 && tw <= 250) {
          try { await api.put("/client/profile", { target_weight_kg: tw }); } catch { /* non-fatal */ }
        }
      }
      if (apply) {
        toast.success(t("calc.applied"));
        if (onApplied) onApplied(data);
        onOpenChange(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setter(false); }
  };

  // ETA estimate — target weight is NEVER part of the kcal calculation
  const eta = useMemo(() => {
    if (!result) return null;
    const tw = parseFloat(String(form.target_weight_kg).replace(",", "."));
    const w = +form.weight_kg;
    if (!Number.isFinite(tw) || !w) return null;
    const weeklyKg = (result.tdee - result.target_kcal) * 7 / 7700; // >0 = losing
    const diff = w - tw; // >0 = needs to lose
    if (Math.abs(diff) < 0.5) return null;
    if (diff > 0 && weeklyKg > 0.05) return { weeks: Math.round(diff / weeklyKg), target: tw };
    if (diff < 0 && weeklyKg < -0.05) return { weeks: Math.round(diff / weeklyKg), target: tw };
    return null;
  }, [result, form.target_weight_kg, form.weight_kg]);

  const chipCls = (active) =>
    `px-3 h-10 rounded-xl text-sm font-medium border transition ${
      active ? "bg-[#5A8B8F] text-white border-[#5A8B8F]" : "bg-white text-slate-600 border-slate-200 hover:border-[#5A8B8F]/40"
    }`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl" data-testid="macro-calc-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Calculator className="h-5 w-5 text-[#5A8B8F]" /> {t("calc.title")}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">{t("calc.subtitle")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Gender + Age + Height + Weight */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500">{t("calc.gender")}</Label>
              <div className="mt-1 grid grid-cols-2 gap-1 p-1 bg-slate-100 rounded-full">
                {["male", "female"].map((g) => (
                  <button key={g} type="button"
                    onClick={() => setForm({ ...form, gender: g })}
                    data-testid={`calc-gender-${g}`}
                    className={`h-9 rounded-full text-sm font-medium transition ${
                      form.gender === g ? "bg-white text-[#2C5254] shadow-sm" : "text-slate-500"
                    }`}
                  >
                    {t(`calc.gender${g === "male" ? "M" : "F"}`)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">{t("calc.age")}</Label>
              <Input type="number" inputMode="numeric" min={10} max={100}
                value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })}
                data-testid="calc-age" placeholder="34" className="mt-1 h-11" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">{t("calc.height")} (cm)</Label>
              <Input type="number" inputMode="numeric" min={100} max={250}
                value={form.height_cm} onChange={(e) => setForm({ ...form, height_cm: e.target.value })}
                data-testid="calc-height" placeholder="175" className="mt-1 h-11" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">{t("calc.weight")} (kg)</Label>
              <Input type="number" inputMode="decimal" step="0.1" min={30} max={300}
                value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })}
                data-testid="calc-weight" placeholder="75" className="mt-1 h-11" />
            </div>
          </div>

          {/* Target weight — ETA/progress only, never part of the kcal calc */}
          <div className="rounded-2xl border border-[#5A8B8F]/25 bg-[#EAF3F4]/40 dark:bg-[#22383a]/60 p-3 flex items-center gap-3">
            <span
              className="h-8 w-8 rounded-full flex items-center justify-center text-white shrink-0"
              style={{ background: "var(--solano-accent, #FC9700)" }}
              aria-hidden
            >
              🎯
            </span>
            <div className="flex-1 min-w-0">
              <Label className="text-xs text-slate-600 dark:text-slate-300 font-semibold">
                {t("calc.targetWeight")} (kg)
              </Label>
              <div className="text-[10px] text-slate-500 dark:text-slate-400 leading-tight">
                {t("calc.targetWeightHint")}
              </div>
            </div>
            <Input
              type="number" inputMode="decimal" step="0.1" min={30} max={250}
              value={form.target_weight_kg}
              onChange={(e) => setForm({ ...form, target_weight_kg: e.target.value })}
              data-testid="calc-target-weight"
              placeholder="70"
              className="h-10 w-20 text-right tabular-nums font-semibold"
            />
          </div>

          {/* Activity — three concrete questions */}
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-500">{t("calc.jobTitle")}</Label>
              <div className="mt-1 grid grid-cols-1 gap-1.5">
                {JOBS.map((j) => (
                  <button key={j.key} type="button"
                    onClick={() => setForm({ ...form, job_activity: j.key })}
                    data-testid={`calc-job-${j.key}`}
                    className={`${chipCls(form.job_activity === j.key)} text-left`}
                  >
                    {t(j.labelKey)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">{t("calc.stepsTitle")}</Label>
              <div className="mt-1 grid grid-cols-4 gap-1.5">
                {STEP_OPTIONS.map((s) => (
                  <button key={s.value} type="button"
                    onClick={() => setForm({ ...form, steps_per_day: s.value })}
                    data-testid={`calc-steps-${s.value}`}
                    className={`${chipCls(+form.steps_per_day === s.value)} px-1 text-xs`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">{t("calc.trainTitle")}</Label>
              <div className="mt-1 grid grid-cols-4 gap-1.5">
                {TRAIN_OPTIONS.map((o) => (
                  <button key={o.value} type="button"
                    onClick={() => setForm({ ...form, trainings_per_week: o.value })}
                    data-testid={`calc-train-${o.value}`}
                    className={chipCls(+form.trainings_per_week === o.value)}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-[11px] text-slate-500 text-right" data-testid="calc-factor">
              {t("calc.factorLabel")}: <span className="font-semibold tabular-nums">×{factor.toFixed(2)}</span>
            </p>
          </div>

          {/* Goal */}
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs text-slate-500">{t("calc.goal")}</Label>
              {isCustomGoal && (
                <span data-testid="calc-goal-custom-badge"
                  className="text-[10px] font-semibold uppercase tracking-wide text-[#E8951C] bg-[#E8951C]/10 rounded-full px-2 py-0.5">
                  {t("calc.goalCustom")}
                </span>
              )}
            </div>
            <div className="mt-1 grid grid-cols-2 gap-1.5">
              {GOALS.map((g) => (
                <button key={g.key} type="button"
                  onClick={() => setGoal(g.key)}
                  data-testid={`calc-goal-${g.key}`}
                  className={`px-3 h-11 rounded-xl text-sm font-medium border text-left flex items-center justify-between gap-2 transition ${
                    form.goal === g.key
                      ? "bg-[#E8951C] text-white border-[#E8951C]"
                      : "bg-white text-slate-600 border-slate-200 hover:border-[#E8951C]/40"
                  }`}
                >
                  <span className="truncate">{t(g.labelKey)}</span>
                  <span className={form.goal === g.key ? "text-white/80" : "text-slate-400"}>{g.detail}</span>
                </button>
              ))}
            </div>

            {/* Deficit speed — only for weight loss */}
            {form.goal === "dimagrimento" && (
              <div className="mt-2">
                <Label className="text-xs text-slate-500">{t("calc.deficitTitle")}</Label>
                <div className="mt-1 grid grid-cols-4 gap-1.5">
                  {DEFICITS.map((d) => (
                    <button key={d.value} type="button"
                      onClick={() => setForm({ ...form, adjustment_pct: d.value })}
                      data-testid={`calc-deficit-${Math.abs(d.value)}`}
                      className={`px-1 h-12 rounded-xl text-xs font-medium border transition flex flex-col items-center justify-center ${
                        !isCustomDeficit && +form.adjustment_pct === d.value
                          ? "bg-[#E8951C] text-white border-[#E8951C]"
                          : "bg-white text-slate-600 border-slate-200 hover:border-[#E8951C]/40"
                      }`}
                    >
                      <span>{t(d.labelKey)}</span>
                      <span className={!isCustomDeficit && +form.adjustment_pct === d.value ? "text-white/80" : "text-slate-400"}>{d.value}%</span>
                    </button>
                  ))}
                  <div
                    data-testid="calc-deficit-custom"
                    className={`px-1 h-12 rounded-xl text-xs font-medium border flex flex-col items-center justify-center ${
                      isCustomDeficit ? "bg-[#E8951C] text-white border-[#E8951C]" : "bg-white text-slate-400 border-slate-200"
                    }`}
                  >
                    <span>{t("calc.deficitCustom")}</span>
                    {isCustomDeficit && <span className="text-white/80">{form.adjustment_pct}%</span>}
                  </div>
                </div>
              </div>
            )}

            {/* Custom kcal adjustment slider */}
            <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-3">
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs text-slate-600 font-medium">{t("calc.customAdj")}</Label>
                <span
                  data-testid="calc-adj-value"
                  className={`text-sm font-semibold tabular-nums ${
                    +form.adjustment_pct > 0 ? "text-emerald-600"
                    : +form.adjustment_pct < 0 ? "text-orange-600" : "text-slate-500"
                  }`}
                >
                  {+form.adjustment_pct > 0 ? "+" : ""}{form.adjustment_pct}%
                </span>
              </div>
              <input
                type="range"
                min="-30" max="30" step="1"
                value={form.adjustment_pct ?? 0}
                onChange={(e) => onSlider(parseInt(e.target.value, 10))}
                data-testid="calc-adj-slider"
                className="w-full accent-[#E8951C]"
              />
              <p className="text-[10px] text-slate-400 mt-1">{t("calc.customAdjHint")}</p>
            </div>
          </div>

          {/* Preview result */}
          {result && (
            <div className="rounded-2xl bg-gradient-to-br from-[#5A8B8F]/5 to-[#E8951C]/5 p-4 border border-slate-200" data-testid="calc-result">
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                <Sparkles className="h-3 w-3 text-[#E8951C]" /> {t("calc.yourNeeds")}
              </p>
              <div className="space-y-1.5 text-sm" data-testid="calc-result-summary">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-slate-500">{t("calc.maintenance")}</span>
                  <span className="font-semibold text-[#1A1D1E] tabular-nums">
                    {result.tdee} kcal
                    <span className="ml-1 text-[10px] font-normal text-slate-400">(BMR {result.bmr} ×{Number(result.activity_factor).toFixed(2)})</span>
                  </span>
                </div>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-slate-500">{t("calc.goalLabel")}</span>
                  <span className="font-semibold text-[#1A1D1E]" data-testid="calc-result-goal">
                    {form.goal === "custom" ? t("calc.goalCustom") : t(GOALS.find((g) => g.key === form.goal)?.labelKey || "calc.goalCustom")}
                    {" · "}{result.adjustment_pct > 0 ? "+" : ""}{result.adjustment_pct}%
                  </span>
                </div>
              </div>
              <div className="mt-3 rounded-xl bg-white border border-slate-100 p-3 text-center">
                <p className="text-[10px] uppercase tracking-wider text-slate-400">{t("calc.recommended")}</p>
                <div className="flex items-baseline gap-1.5 justify-center">
                  <span className="font-heading text-4xl font-bold text-[#2C5254]" data-testid="calc-result-kcal">
                    {result.target_kcal}
                  </span>
                  <span className="text-sm text-slate-500">kcal/{t("calc.day")}</span>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2" data-testid="calc-macros-editor">
                {[
                  { key: "carbs_pct",   label: t("cliDash.carbs"),   val: result.carbs_g,   pct: form.carbs_pct ?? result.carbs_pct,     exact: result.carbs_pct_exact,   color: "bg-purple-400" },
                  { key: "protein_pct", label: t("cliDash.protein"), val: result.protein_g, pct: form.protein_pct ?? result.protein_pct, exact: result.protein_pct_exact, color: "bg-sky-400" },
                  { key: "fat_pct",     label: t("cliDash.fat"),     val: result.fat_g,     pct: form.fat_pct ?? result.fat_pct,         exact: result.fat_pct_exact,     color: "bg-lime-400" },
                ].map((m) => (
                  <div key={m.key} className="text-center rounded-xl bg-white p-2 border border-slate-100">
                    <p className="font-heading text-lg font-semibold text-[#1A1D1E]">{m.val}<span className="text-xs text-slate-400">g</span></p>
                    <div className={`h-0.5 w-8 mx-auto ${m.color} rounded-full my-1`} />
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">{m.label}</p>
                    <div className="mt-1 flex items-center gap-1">
                      <input
                        type="number" min="0" max="80" step="1"
                        value={m.pct}
                        onChange={(e) => {
                          const v = e.target.value === "" ? 0 : Math.max(0, Math.min(80, parseInt(e.target.value, 10) || 0));
                          setMacrosEdited(true);
                          setForm((f) => ({
                            ...f,
                            protein_pct: f.protein_pct ?? result.protein_pct,
                            carbs_pct: f.carbs_pct ?? result.carbs_pct,
                            fat_pct: f.fat_pct ?? result.fat_pct,
                            [m.key]: v,
                          }));
                        }}
                        data-testid={`calc-${m.key}-input`}
                        className="h-7 w-full text-center text-xs font-semibold rounded-md border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#5A8B8F] outline-none tabular-nums"
                      />
                      <span className="text-[10px] text-slate-400">%</span>
                    </div>
                    {!macrosEdited && m.exact != null && (
                      <p className="text-[9px] text-slate-400 mt-0.5 tabular-nums" data-testid={`calc-${m.key}-exact`}>{Number(m.exact).toFixed(1)}%</p>
                    )}
                  </div>
                ))}
              </div>

              {/* Sum feedback when user edited the split */}
              {macrosEdited && (() => {
                const sum = (+(form.protein_pct ?? result.protein_pct) || 0)
                  + (+(form.carbs_pct   ?? result.carbs_pct)   || 0)
                  + (+(form.fat_pct     ?? result.fat_pct)     || 0);
                if (sum === 100) return null;
                return (
                  <p data-testid="calc-macros-sum-warning"
                     className={`text-[11px] mt-2 ${Math.abs(sum - 100) > 5 ? "text-orange-600 font-medium" : "text-slate-500"}`}>
                    {t("calc.sumHint", { sum })}
                  </p>
                );
              })()}

              {/* ETA — clearly marked as an estimate */}
              {eta && (
                <div className="mt-3 rounded-xl bg-white border border-slate-100 p-2.5" data-testid="calc-eta">
                  <p className="text-xs font-semibold text-[#2C5254]">
                    {t("calc.etaWeeks", { weeks: eta.weeks, target: eta.target })}
                  </p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{t("calc.etaNote")}</p>
                </div>
              )}

              {/* Double-counting hint when training is inside the factor */}
              {result.training_in_factor && (
                <div className="mt-2 flex items-start gap-1.5 rounded-xl bg-amber-50 border border-amber-200 p-2.5" data-testid="calc-dblcount-hint">
                  <Info className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-[11px] text-amber-800 leading-snug">{t("calc.dblCountHint")}</p>
                </div>
              )}

              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  onClick={() => calculate(false)}
                  disabled={loading}
                  data-testid="calc-recompute-btn"
                  className="text-xs font-semibold text-[#2C5254] hover:text-[#5A8B8F] disabled:opacity-50"
                >
                  {loading ? "…" : "↻ " + t("calc.recompute")}
                </button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          {onOpenPlan && (
            <button
              type="button"
              onClick={onOpenPlan}
              data-testid="open-nutrition-plan-btn"
              className="w-full sm:w-auto rounded-full border border-[#5A8B8F]/40 bg-[#5A8B8F]/8 px-4 h-10 text-xs font-bold text-[#2C5254] hover:bg-[#5A8B8F]/15 transition"
            >
              {t("cliDash.planCtaTitle")}
            </button>
          )}
          {!result ? (
            <Button onClick={() => calculate(false)} disabled={!canCalc || loading}
              data-testid="calc-preview-btn"
              className="w-full rounded-full bg-[#5A8B8F] hover:bg-[#4a7679] text-white h-11">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4 mr-2" />}
              {t("calc.calculate")}
            </Button>
          ) : (
            <>
              <Button variant="outline"
                onClick={() => {
                  if (onManualEntry) onManualEntry(result.target_kcal);
                  else { setResult(null); setMacrosEdited(false); setForm((f) => ({ ...f, protein_pct: null, carbs_pct: null, fat_pct: null })); }
                }}
                data-testid="calc-manual-adjust-btn"
                className="rounded-full">
                {t("calc.adjustManually")}
              </Button>
              <Button onClick={() => calculate(true)} disabled={applying}
                data-testid="calc-apply-btn"
                className="rounded-full bg-[#E8951C] hover:bg-[#c47a10] text-white flex-1">
                {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                {t("calc.applyComputed")}
              </Button>
            </>
          )}
        </DialogFooter>

        {/* Secondary path: user already knows their calorie target */}
        {!result && onManualEntry && (
          <button
            type="button"
            onClick={() => onManualEntry(null)}
            data-testid="calc-manual-entry-link"
            className="w-full text-center text-xs text-slate-500 underline underline-offset-2 hover:text-[#2C5254] transition pb-1"
          >
            {t("calc.manualLink")}
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}
